import React, { useEffect, useState } from 'react';
import type { MenuProps } from 'antd';
import { Tooltip, Button, Dropdown, Checkbox } from 'antd';
import { BookOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { getCompanyID, getCompany, getCommon, getCompanySettings, getUserRole, getUser } from '../../../../redux/selectors';
import { FeatureFlag } from '../../../../utils/feature-flags/FeatureFlagsProvider';
import { TimelineType } from './types/timeline';
import { getIsDrawerVisible, getIsFilterDrawerPinned, getVisibleTimelines, getIsCompactView } from './redux-saga/selectors';
import Guard from '../../../permissions/crew-schedule-v2-guard';
import { scheduleActions } from './redux-saga/actions';
import { bulkModalEntryActions } from './components/Modals/BulkScheduleModal/redux-saga/actions';
import MODAL_MODE from '../../common/constants';
import api from '../../../../services/api';
import styles from './components/Timeline/TimelineSelector/TimelineSelector.module.scss';

import { setState, versioningActions } from './components/Versioning/redux-saga/actions';
import { IDs } from '../../../../constants';

import {
  setCurrentDutyScheduleEntryAction,
  setCurrentMaintenanceEntryAction,
  setCurrentScheduleEntryAction,
} from '../../../../redux/pages/crew-scheduling-v3/actions';
import {
  openCrewScheduleModalAction,
} from '../../../../redux/pages/crew-scheduling-v3/modals/ScheduleEntryModal/actions';
import {
  openDutyScheduleModalAction,
} from '../../../../redux/pages/crew-scheduling-v3/modals/DutyScheduleEntryModal/actions';
import dutyTimeModalActions, {
  setCurrentDutyTimeEntry,
} from '../../../../redux/pages/crew-scheduling-v3/modals/DutyTimeEntryModal/actions';
import { DutyTimeEntry } from '../../../../redux/pages/crew-scheduling-v3/scheduleEntries/types/DutyTimeEntry';
import {
  openMaintenanceScheduleModalAction,
} from '../../../../redux/pages/crew-scheduling-v3/modals/MaintenanceEntryModal/actions';
import { openMTSModal } from './components/MTSSchedule/redux-saga/actions';
import { scheduleTemplateModalActions } from './components/Modals/ScheduleTemplateModal/redux-saga/actions';
import { openModal } from '../../../../redux/pages/crew-scheduling-v3/modals/ExtensionPeriodsModal/actions';
import { isMobile } from 'react-device-detect';

const ActionsDropdown: React.FC = () => {
  const dispatch = useDispatch();
  const isDrawerVisible = useSelector(getIsDrawerVisible);
  const { featureFlags } = useSelector(getCommon);
  const companySettings = useSelector(getCompanySettings);
  const visibleTimelines = useSelector(getVisibleTimelines);
  const userRoles = useSelector(getUserRole);
  const guard = new Guard('crewScheduleV2');
  const isFilterPinned = useSelector(getIsFilterDrawerPinned);

  const isAircraftTimelineVisible = visibleTimelines.includes(TimelineType.Aircraft);

  const items: MenuProps['items'] = [
    featureFlags[FeatureFlag.AircraftPaneScheduling] &&
    userRoles.includes('admin') && {
      key: 'generate-schedule',
      label: 'Generate Schedule',
      onClick: () => {
        dispatch(
          bulkModalEntryActions.setAircraftModalMode({ open: true, mode: MODAL_MODE.create }),
        );
        if (isDrawerVisible && !isFilterPinned) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    companySettings?.AllowGroupSchedule && {
      key: 'generate-group-schedule',
      label: 'Generate Group Schedule',
      onClick: () => {
        dispatch(scheduleActions.setGroupModalMode({ mode: MODAL_MODE.create, open: true }));
        if (isDrawerVisible && !isFilterPinned) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    isAircraftTimelineVisible &&
    guard.canAccess('actions.addAircraftContract') &&
    userRoles.includes('admin') && {
      key: 'add-aircraft-contract',
      label: 'Add Aircraft Contract',
      onClick: () => {
        dispatch(
          scheduleActions.setAircraftContractModalMode({ open: true, mode: MODAL_MODE.create }),
        );
        dispatch(scheduleActions.setEditAircraftContractEntry({}));
        if (isDrawerVisible && !isFilterPinned) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    guard.canAccess('actions.addPilotContract') &&
    userRoles.includes('admin') && {
      key: 'addPilotContract',
      label: 'Add Pilot Contract',
      onClick: () => {
        dispatch(
          scheduleActions.setPilotContractModalMode({ open: true, mode: MODAL_MODE.create }),
        );
        dispatch(scheduleActions.setPilotContractEntryForModal({}));
        if (isDrawerVisible && !isFilterPinned) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    guard.canAccess('actions.addScheduleEntry') &&
    userRoles.includes('admin') && {
      key: 'addScheduleEntry',
      label: 'Add Schedule Entry',
      onClick: () => {
        dispatch(setCurrentScheduleEntryAction({}));
        dispatch(openCrewScheduleModalAction(MODAL_MODE.create));
        if (isDrawerVisible && !isFilterPinned) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    guard.canAccess('actions.addDutyEntry') &&
    (userRoles.includes('admin') || companySettings?.AllowPilotPlannedDutyManagement) && {
      key: 'addPlannedDuty',
      label: 'Add Planned Duty',
      onClick: () => {
        dispatch(setCurrentDutyScheduleEntryAction({ isNewEntry: true }));
        dispatch(openDutyScheduleModalAction(MODAL_MODE.create));
        if (isDrawerVisible && !isFilterPinned) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    guard.canAccess('actions.addDutyTime') &&
    (userRoles.includes('admin') || companySettings?.AllowPilotDutyManagement) && {
      key: 'addActualDuty',
      label: 'Add Actual Duty',
      onClick: () => {
        dispatch(dutyTimeModalActions.openModalAction(MODAL_MODE.create));
        dispatch(setCurrentDutyTimeEntry({} as DutyTimeEntry));
        if (isDrawerVisible && !isFilterPinned) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    guard.canAccess('actions.addMaintenanceEntry') &&
    userRoles.includes('admin') &&
    companySettings?.MaintenanceSchedule && {
      key: 'addMaintenanceEntry',
      label: 'Add Maintenance Entry',
      onClick: () => {
        dispatch(setCurrentMaintenanceEntryAction({}));
        dispatch(openMaintenanceScheduleModalAction(MODAL_MODE.create));
      },
    },
    companySettings?.MTSSchedule && userRoles?.includes('admin') && {
      key: 'generate-mts-schedule',
      label: 'Generate MTS Schedule',
      onClick: () => {
        dispatch(openMTSModal(MODAL_MODE.create));
        if (isDrawerVisible) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    companySettings?.ScheduleTemplates && userRoles?.includes('admin') && {
      key: 'generate-schedule-template',
      label: 'Generate Booking Slot',
      onClick: () => {
        dispatch(
          scheduleTemplateModalActions.setScheduleTemplateModalMode({
            open: true,
            mode: MODAL_MODE.create,
          }));
        if (isDrawerVisible) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    (companySettings?.SalesModule && (userRoles?.includes('admin') || userRoles?.includes('subContractor'))) && {
      key: 'generate-schedule-base',
      label: companySettings?.BaseScheduleParts?.Routing ? 'Add Booking' : 'Add Base Schedule',
      onClick: () => {
        dispatch(scheduleActions.setGroupModalMode({ open: true, mode: MODAL_MODE.create }));
        if (isDrawerVisible) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
    companySettings?.Enable722CARValidation && userRoles?.includes('admin') && {
      key: 'generate-extension-period',
      label: 'Add Extension Period',
      onClick: () => {
        dispatch(openModal(MODAL_MODE.create));
        if (isDrawerVisible) {
          dispatch(scheduleActions.setIsDrawerVisible(false));
        }
      },
    },
  ].filter(Boolean);

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Button type="primary" style={{ width: '100%' }}>Actions</Button>
    </Dropdown>
  );
};

export const VersionButton: React.FC = () => {
  const dispatch = useDispatch();
  const visibleTimelines = useSelector(getVisibleTimelines);
  const userRoles = useSelector(getUserRole);
  const companyID = useSelector(getCompanyID);
  const user = useSelector(getUser);

  const publishSatelliteVisibility = value => {
    dispatch(versioningActions.setState({ satelliteVisibility: value }));
  };
  const [sharedSandboxes, setSharedSandboxes] = useState([]);

  useEffect(() => {
    const fetchSharedSandboxes = async () => {
      try {
        const response = await api.get(
          `/v1/companies/${companyID}/crew-schedule-sandbox/shared-sandboxes`,
          {
            params: {
              user_id: user.id,
            },
          },
        );
        setSharedSandboxes(response?.data);
      } catch (e) {
        console.error('Failed to fetch shared sandboxes', e);
      }
    };
    fetchSharedSandboxes();
  }, []);

  return (
    <Tooltip title="View published versions or create new version">
      <Button
        id={IDs.versioning.versionsBtn}
        type="primary"
        onClick={() => {
          publishSatelliteVisibility(true);
        }}
      >
        <BookOutlined />
        {sharedSandboxes?.length > 0 && !userRoles.includes('admin')
          ? 'Shared Sandboxes'
          : 'Versions'}
      </Button>
    </Tooltip>
  );
};

export const MinViewCheckbox: React.FC = () => {
  const dispatch = useDispatch();
  const isCompactView = useSelector(getIsCompactView);

  return (
    <Checkbox
      className={styles.TimelineSelector__checkbox}
      skipGroup
      checked={isCompactView}
      onChange={e => dispatch(scheduleActions.setSwitchCompactView(e.target.checked))}
    >
      <FullscreenExitOutlined /> Minimized View
    </Checkbox>
  );
};

export default ActionsDropdown;
